import { Container, Graphics, Texture } from 'pixi.js';
import { Cell, type WinState } from './Cell.ts';
import { SYMBOL_IDS } from '../theme/symbols.ts';

type Phase = 'idle' | 'spin' | 'brake' | 'settle';

const SPIN_SPEED = 2.0; // px per ms
const BRAKE_MS = 220;
const SETTLE_MS = 260;
const ROWS = 3;

/** One reel column: scrolls with motion blur, then snaps to the target symbols. */
export class Reel {
  readonly view = new Container();
  private readonly cells: Cell[] = [];
  private readonly cell: number;
  private phase: Phase = 'idle';
  private offset = 0;
  private speed = 0;
  private brakeT = 0;
  private settleT = 0;
  private target: string[] = [];
  private onStopped: (() => void) | null = null;
  private brakeDelay = 0;
  private readonly antic = new Graphics();
  private anticOn = false;
  private anticT = 0;

  constructor(cell: number, textures: Record<string, Texture>) {
    this.cell = cell;
    for (let i = 0; i < ROWS + 1; i++) {
      const c = new Cell(cell, textures);
      c.set(SYMBOL_IDS[(Math.random() * SYMBOL_IDS.length) | 0]);
      this.cells.push(c);
      this.view.addChild(c.view);
    }
    const mask = new Graphics().rect(0, 0, cell, ROWS * cell).fill(0xffffff);
    this.view.addChild(mask);
    this.view.mask = mask;
    // Anticipation frame: inset so the stroke survives the mask.
    this.antic.roundRect(3, 3, cell - 6, ROWS * cell - 6, 10).stroke({ width: 5, color: 0xffd45e });
    this.antic.visible = false;
    this.view.addChild(this.antic);
    this.reposition();
  }

  private reposition(stretch = 1): void {
    for (let i = 0; i < this.cells.length; i++) {
      const c = this.cells[i];
      c.view.x = 0;
      c.view.y = (i - 1) * this.cell + this.offset;
      c.setMotion(stretch);
    }
  }

  private recycle(): void {
    const c = this.cells.pop()!;
    this.cells.unshift(c);
    c.set(SYMBOL_IDS[(Math.random() * SYMBOL_IDS.length) | 0]);
  }

  start(): void {
    if (this.phase === 'spin' || this.phase === 'brake') return;
    this.phase = 'spin';
    this.speed = SPIN_SPEED;
    this.clearWinning();
  }

  stop(target: string[], onStopped: () => void, extraBrakeMs = 0): void {
    this.target = target;
    this.onStopped = onStopped;
    this.phase = 'brake';
    this.brakeT = 0;
    this.brakeDelay = extraBrakeMs;
  }

  /** Pulsing golden frame while scatters tease the remaining reels. */
  setAnticipation(on: boolean): void {
    this.anticOn = on;
    this.antic.visible = on;
    this.anticT = 0;
  }

  private applyTarget(): void {
    this.cells[1].set(this.target[0]);
    this.cells[2].set(this.target[1]);
    this.cells[3].set(this.target[2]);
    this.cells[0].set(SYMBOL_IDS[(Math.random() * SYMBOL_IDS.length) | 0]);
  }

  update(dt: number): void {
    if (this.anticOn) {
      this.anticT += dt;
      this.antic.alpha = 0.6 + 0.3 * Math.sin(this.anticT / 110);
    }
    switch (this.phase) {
      case 'spin':
      case 'brake': {
        if (this.phase === 'brake') {
          this.brakeT += dt;
          // brakeDelay front-loads extra full-speed time before the normal ramp.
          const bt = this.brakeT - this.brakeDelay;
          this.speed = bt <= 0 ? SPIN_SPEED : SPIN_SPEED * Math.max(0, 1 - bt / BRAKE_MS);
        }
        this.offset += this.speed * dt;
        while (this.offset >= this.cell) {
          this.offset -= this.cell;
          this.recycle();
        }
        this.reposition(1 + Math.min(this.speed * 0.25, 0.55));
        if (this.phase === 'brake' && this.brakeT - this.brakeDelay >= BRAKE_MS) {
          this.offset = 0;
          this.applyTarget();
          this.phase = 'settle';
          this.settleT = 0;
        }
        break;
      }
      case 'settle': {
        this.settleT += dt;
        const p = Math.min(1, this.settleT / SETTLE_MS);
        this.offset = Math.sin(p * Math.PI) * this.cell * 0.16 * (1 - p);
        this.reposition(1);
        if (p >= 1) {
          this.offset = 0;
          this.reposition(1);
          this.phase = 'idle';
          this.setAnticipation(false);
          const cb = this.onStopped;
          this.onStopped = null;
          cb?.();
        }
        break;
      }
      case 'idle':
        // Win pulse only runs while idle so it can't fight the motion stretch.
        for (let r = 0; r < ROWS; r++) this.cells[r + 1].updateFx(dt);
        break;
    }
  }

  /** Visible cell at row 0..2 (cells[0] is the off-screen spare). */
  getCell(row: number): Cell {
    return this.cells[row + 1];
  }

  setWinningRow(row: number, on: boolean): void {
    this.cells[row + 1].setWinning(on);
  }

  setWinState(row: number, state: WinState): void {
    this.cells[row + 1].setWinState(state);
  }

  clearWinning(): void {
    for (let r = 0; r < ROWS; r++) this.cells[r + 1].setWinState('none');
  }

  get spinning(): boolean {
    return this.phase !== 'idle';
  }
}
